import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Save, ChevronDown, Loader2 } from 'lucide-react';

// A green split "Save" button whose down-arrow expands a connected panel that
// reveals "Save As" directly beneath it — the button and the panel read as one
// rounded card (green top, popover-colored bottom). The panel is portaled and
// fixed-positioned so a scrolling/overflow-hidden toolbar can't clip it. Used on
// the Semantic Model editor and the Dashboards & Reports builder.
export default function SaveSplitButton({
  onSave,
  onSaveAs,
  saving = false,
  saveLabel = 'Save',
  saveAsLabel = 'Save As',
  disabled = false,
  saveAsDisabled = false,
  fullWidth = false,
  testIdPrefix = 'save',
  title = 'Save all changes',
  saveAsTitle = 'Save a copy under a new name',
}) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const ref = useRef(null);
  const panelRef = useRef(null);

  const place = useCallback(() => {
    if (ref.current) setRect(ref.current.getBoundingClientRect());
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onDoc = (e) => {
      const t = e.target;
      if (ref.current && !ref.current.contains(t) && !(panelRef.current && panelRef.current.contains(t))) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, place]);

  const green = 'bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed';

  return (
    <div ref={ref} className={`relative ${fullWidth ? 'block w-full' : 'inline-block'}`}>
      <div className={`flex items-stretch shadow-sm ${fullWidth ? 'w-full' : ''}`}>
        <button
          type="button"
          onClick={onSave}
          disabled={disabled || saving}
          title={title}
          data-testid={`${testIdPrefix}-btn`}
          className={`flex items-center justify-center gap-1.5 h-7 px-3 text-xs font-semibold active:scale-[0.98] ${green} ${fullWidth ? 'flex-1' : ''} ${open ? 'rounded-tl-lg' : 'rounded-l-lg'}`}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {saveLabel}
        </button>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          disabled={disabled}
          aria-label="More save options"
          aria-expanded={open}
          data-testid={`${testIdPrefix}-menu-btn`}
          className={`flex items-center justify-center px-1.5 h-7 border-l border-primary-foreground/25 ${green} ${open ? 'rounded-tr-lg' : 'rounded-r-lg'}`}
        >
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && rect && createPortal(
        <div
          ref={panelRef}
          className="fixed z-[9999] bg-popover border border-t-0 border-border rounded-b-lg shadow-lg overflow-hidden"
          style={{ top: rect.bottom, left: rect.left, width: rect.width }}
        >
          <button
            type="button"
            onClick={() => { setOpen(false); onSaveAs && onSaveAs(); }}
            disabled={saveAsDisabled}
            title={saveAsTitle}
            data-testid={`${testIdPrefix}-as-item`}
            className="w-full text-left px-3 h-7 flex items-center text-xs font-semibold text-popover-foreground hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveAsLabel}
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}
